import { useState } from 'react'
import { useQuery } from 'react-query'
import { churchApi } from '../services/api'
import LoadingSpinner from '../components/LoadingSpinner'
import { MapPinIcon, PhoneIcon, EnvelopeIcon, UserGroupIcon } from '@heroicons/react/24/outline'

interface Church {
  id: number
  name: string
  location: string
  contact_email: string
  contact_phone: string
  coordinator_name?: string
  participant_count?: number
  status: 'approved' | 'pending' | 'rejected'
  created_at: string
}

const ChurchesPage = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState<'all' | 'approved' | 'pending'>('approved')

  const { data: churches = [], isLoading, error } = useQuery(
    'churches',
    churchApi.getChurches,
    {
      select: (response) => response.data
    }
  )

  const filteredChurches = churches.filter((church: Church) => {
    const matchesSearch = church.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         church.location?.toLowerCase().includes(searchTerm.toLowerCase())
    
    const matchesStatus = filterStatus === 'all' || church.status === filterStatus
    
    return matchesSearch && matchesStatus
  })

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Unable to load churches</h2>
          <p className="text-gray-600">Please try again later.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Participating Churches</h1>
          <p className="mt-2 text-gray-600">
            Discover the churches participating in our Mahragan competitions
          </p>
        </div>

        {/* Search and Filter */}
        <div className="bg-white p-6 rounded-lg shadow mb-8">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-2">
                Search Churches
              </label>
              <input
                id="search"
                type="text"
                placeholder="Search by church name or location..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            
            <div className="sm:w-48">
              <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-2">
                Status Filter
              </label>
              <select
                id="status"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value as 'all' | 'approved' | 'pending')}
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Churches</option>
                <option value="approved">Approved</option>
                <option value="pending">Pending Approval</option>
              </select>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <UserGroupIcon className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Total Churches
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {churches.length}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="h-6 w-6 bg-green-500 rounded-full"></div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Approved Churches
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {churches.filter((c: Church) => c.status === 'approved').length}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="h-6 w-6 bg-yellow-500 rounded-full"></div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Pending Approval
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {churches.filter((c: Church) => c.status === 'pending').length}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Churches Grid */}
        {filteredChurches.length === 0 ? (
          <div className="text-center py-12">
            <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No churches found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || filterStatus !== 'all' 
                ? 'Try adjusting your search or filter criteria.'
                : 'Churches will appear here once they register for the Mahragan.'
              }
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredChurches.map((church: Church) => (
              <div
                key={church.id}
                className="bg-white overflow-hidden shadow rounded-lg hover:shadow-md transition-shadow"
              >
                <div className="p-6">
                  {/* Status Badge */}
                  <div className="flex justify-between items-start mb-4">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        church.status === 'approved'
                          ? 'bg-green-100 text-green-800'
                          : church.status === 'pending'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {church.status.charAt(0).toUpperCase() + church.status.slice(1)}
                    </span>
                  </div>

                  {/* Church Name */}
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    {church.name}
                  </h3>

                  {/* Church Details */}
                  <div className="space-y-3 text-sm text-gray-600">
                    {church.location && (
                      <div className="flex items-center">
                        <MapPinIcon className="h-4 w-4 mr-2 text-gray-400" />
                        <span>{church.location}</span>
                      </div>
                    )}

                    {church.contact_phone && (
                      <div className="flex items-center">
                        <PhoneIcon className="h-4 w-4 mr-2 text-gray-400" />
                        <a 
                          href={`tel:${church.contact_phone}`}
                          className="hover:text-blue-600"
                        >
                          {church.contact_phone}
                        </a>
                      </div>
                    )}

                    {church.contact_email && (
                      <div className="flex items-center">
                        <EnvelopeIcon className="h-4 w-4 mr-2 text-gray-400" />
                        <a 
                          href={`mailto:${church.contact_email}`}
                          className="hover:text-blue-600"
                        >
                          {church.contact_email}
                        </a>
                      </div>
                    )}

                    {church.coordinator_name && (
                      <div className="flex items-center">
                        <UserGroupIcon className="h-4 w-4 mr-2 text-gray-400" />
                        <span>Coordinator: {church.coordinator_name}</span>
                      </div>
                    )}
                  </div>

                  {/* Participants Count */}
                  {church.participant_count !== undefined && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-500">Participants</span>
                        <span className="font-medium text-gray-900">
                          {church.participant_count}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Registration Date */}
                  <div className="mt-2 text-xs text-gray-500">
                    Registered: {new Date(church.created_at).toLocaleDateString()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Call to Action */}
        <div className="mt-12 text-center">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h3 className="text-lg font-medium text-blue-900 mb-2">
              Is your church interested in participating?
            </h3>
            <p className="text-blue-700 mb-4">
              Join our growing community of churches and give your participants the opportunity to showcase their talents.
            </p>
            <button className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors">
              Contact Us
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ChurchesPage